import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  .report-page {
    font-family: 'Inter', system-ui, sans-serif;
    background: #0d0f1a;
    min-height: 100vh;
    padding: 32px 24px 80px;
    color: #e2e8f0;
  }
  .report-inner { max-width: 1000px; margin: 0 auto; }

  /* Header */
  .report-header { display: flex; align-items: center; gap: 14px; margin-bottom: 32px; }
  .report-badge {
    background: #6366f1; color: #fff; font-size: 10px; font-weight: 700;
    padding: 3px 8px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.5px;
  }

  /* Search area */
  .search-card {
    background: #161929; border: 1px solid rgba(99,102,241,0.15);
    border-radius: 20px; padding: 28px; margin-bottom: 32px;
  }
  .search-box { display: flex; gap: 12px; }
  .search-input {
    flex: 1; background: #0d0f1a; border: 1px solid rgba(99,102,241,0.2);
    border-radius: 12px; padding: 12px 18px; color: #fff; font-family: inherit;
  }
  .search-btn {
    background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none;
    border-radius: 12px; padding: 12px 24px; color: #fff; font-weight: 600; cursor: pointer;
  }

  /* Controls */
  .report-controls { display: flex; gap: 10px; margin-bottom: 24px; }
  .btn {
    background: #161929; border: 1px solid rgba(99,102,241,0.2);
    border-radius: 10px; padding: 10px 20px; color: #e2e8f0; cursor: pointer; font-size: 13px; font-weight: 500;
  }
  .btn-primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none; color: #fff; }

  /* Preview area */
  .preview-wrap {
    background: #ffffff; border-radius: 16px; padding: 40px; color: #1e293b;
    box-shadow: 0 10px 25px rgba(0,0,0,0.2); margin-bottom: 32px;
  }

  /* Email content styles */
  .email-subj { font-size: 18px; font-weight: 800; color: #4f46e5; margin-bottom: 20px; border-bottom: 1px solid #e2e8f0; padding-bottom: 14px; }
  .email-section { font-size: 12px; font-weight: 700; text-transform: uppercase; color: #4f46e5; margin: 30px 0 12px; letter-spacing: 1px; }
  .email-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 10px 30px;
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;
  }
  .email-table { width: 100%; border-collapse: collapse; font-size: 12px; margin: 12px 0; border: 1px solid #e2e8f0; }
  .email-table th { background: #f1f5f9; padding: 10px; text-align: left; color: #475569; border-bottom: 2px solid #6366f1; }
  .email-table td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
  
  /* Badges */
  .p-badge { padding: 2px 8px; border-radius: 100px; font-size: 10px; font-weight: 700; text-transform: uppercase; }
  .p-active { background: #dcfce7; color: #166534; }
  .p-expired { background: #fee2e2; color: #991b1b; }
  .p-inactive { background: #f1f5f9; color: #475569; }

  /* Stats cards */
  .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 16px; margin-bottom: 32px; }
  .stat-card { background: #161929; border: 1px solid rgba(99,102,241,0.15); border-radius: 16px; padding: 20px; text-align: center; }
  .stat-val { font-size: 24px; font-weight: 800; margin-bottom: 4px; }
  .stat-lab { font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; }
`;

const AdminReport = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copyStatus, setCopyStatus] = useState('Copy Email Body');
  const previewRef = useRef(null);

  const fetchReport = async () => {
    if (!email) return;
    setLoading(true);
    setError('');
    setReport(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/report?email=${encodeURIComponent(email)}`, { withCredentials: true });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const copyRichContent = () => {
    if (!previewRef.current) return;
    
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(previewRef.current);
    selection.removeAllRanges();
    selection.addRange(range);
    
    try {
      document.execCommand('copy');
      setCopyStatus('✓ Rich Report Copied!');
      setTimeout(() => setCopyStatus('Copy Email Body'), 3000);
    } catch (err) {
      alert('Manual copy required.');
    }
    selection.removeAllRanges();
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  
  const getShortUrl = (link) => {
    const base = 'rdrto.com';
    if (link.url_type === 'brand_path' && link.brand_name) return `${base}/${link.brand_name}/${link.short_code}`;
    if (link.url_type === 'brand_subdomain' && link.brand_name) return `${link.brand_name}.${base}/${link.short_code}`;
    return `${base}/${link.short_code}`;
  };

  return (
    <div className="report-page">
      <style>{STYLES}</style>
      <div className="report-inner">
        
        <div className="report-header">
          <div className="report-badge">Admin System</div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 900 }}>User Link Report</h1>
        </div>

        <div className="search-card">
          <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#94a3b8' }}>Generate a professional performance report for any user email.</p>
          <div className="search-box">
            <input 
              className="search-input"
              type="email" 
              placeholder="user@example.com" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchReport()}
            />
            <button className="search-btn" onClick={fetchReport} disabled={loading}>
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
          {error && <p style={{ color: '#f87171', fontSize: '13px', marginTop: '12px' }}>{error}</p>}
        </div>

        {report && (
          <div className="report-content">
            
            <div className="report-controls">
              <button className="btn btn-primary" onClick={copyRichContent}>{copyStatus}</button>
              <button className="btn" onClick={() => navigate('/admin')}>← Back to Dashboard</button>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-val" style={{ color: '#818cf8' }}>{report.links.length}</div>
                <div className="stat-lab">Total Links</div>
              </div>
              <div className="stat-card">
                <div className="stat-val" style={{ color: '#10b981' }}>{report.links.filter(l => l.status === 'active').length}</div>
                <div className="stat-lab">Active</div>
              </div>
              <div className="stat-card">
                <div className="stat-val" style={{ color: '#f87171' }}>{report.links.filter(l => l.status === 'expired').length}</div>
                <div className="stat-lab">Expired</div>
              </div>
              <div className="stat-card">
                <div className="stat-val" style={{ color: '#3b82f6' }}>{report.links.reduce((s,l) => s + parseInt(l.total_clicks), 0).toLocaleString()}</div>
                <div className="stat-lab">Total Clicks</div>
              </div>
            </div>

            <div className="preview-wrap" ref={previewRef}>
              <div className="email-subj">📊 Your RDRTO Link Performance Report</div>
              <p style={{ margin: '0 0 20px' }}>Dear {report.user.username || report.user.email.split('@')[0]},<br/>
              <span style={{ color: '#64748b', fontSize: '12px' }}>Please find your personalised RDRTO link performance summary below.</span></p>

              <div className="email-section">Account Overview</div>
              <table style={{ width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                <tbody>
                  <tr>
                    <td style={{ color: '#64748b', fontSize: '12px', width: '25%' }}>Email</td>
                    <td style={{ fontWeight: 600, fontSize: '12px', width: '25%' }}>{report.user.email}</td>
                    <td style={{ color: '#64748b', fontSize: '12px', width: '25%' }}>Plan</td>
                    <td style={{ fontWeight: 600, fontSize: '12px', width: '25%' }}>{report.user.plan.toUpperCase()}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#64748b', fontSize: '12px' }}>Member Since</td>
                    <td style={{ fontWeight: 600, fontSize: '12px' }}>{fmtDate(report.user.created_at)}</td>
                    <td style={{ color: '#64748b', fontSize: '12px' }}>Total Links</td>
                    <td style={{ fontWeight: 600, fontSize: '12px' }}>{report.user.total_links}</td>
                  </tr>
                </tbody>
              </table>

              <div className="email-section">Link Details</div>
              <table className="email-table">
                <thead>
                  <tr>
                    <th style={{ background: '#f1f5f9', padding: '10px', textAlign: 'left', color: '#475569', borderBottom: '2px solid #6366f1' }}>Short Link</th>
                    <th style={{ background: '#f1f5f9', padding: '10px', textAlign: 'left', color: '#475569', borderBottom: '2px solid #6366f1' }}>Clicks</th>
                    <th style={{ background: '#f1f5f9', padding: '10px', textAlign: 'left', color: '#475569', borderBottom: '2px solid #6366f1' }}>Status</th>
                    <th style={{ background: '#f1f5f9', padding: '10px', textAlign: 'left', color: '#475569', borderBottom: '2px solid #6366f1' }}>Created</th>
                    <th style={{ background: '#f1f5f9', padding: '10px', textAlign: 'left', color: '#475569', borderBottom: '2px solid #6366f1' }}>Expires</th>
                    <th style={{ background: '#f1f5f9', padding: '10px', textAlign: 'left', color: '#475569', borderBottom: '2px solid #6366f1' }}>Password</th>
                    <th style={{ background: '#f1f5f9', padding: '10px', textAlign: 'left', color: '#475569', borderBottom: '2px solid #6366f1' }}>Self-Healing</th>
                  </tr>
                </thead>
                <tbody>
                  {report.links.map(l => (
                    <tr key={l.id}>
                      <td style={{ color: '#4f46e5', fontWeight: 600, padding: '10px', borderBottom: '1px solid #e2e8f0' }}>{getShortUrl(l)}</td>
                      <td style={{ fontWeight: 700, padding: '10px', borderBottom: '1px solid #e2e8f0' }}>{parseInt(l.total_clicks).toLocaleString()}</td>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}><span className={`p-badge p-${l.status}`}>{l.status}</span></td>
                      <td style={{ color: '#64748b', padding: '10px', borderBottom: '1px solid #e2e8f0' }}>{fmtDate(l.created_at)}</td>
                      <td style={{ color: '#64748b', padding: '10px', borderBottom: '1px solid #e2e8f0' }}>{l.expires_at ? fmtDate(l.expires_at) : '—'}</td>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>{l.has_password ? <span style={{ color: '#f59e0b' }}>🔒 Yes</span> : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>{l.healing_enrolled ? <span style={{ color: '#10b981', fontWeight: 600 }}>Enrolled</span> : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="email-section">Self-Healing Links</div>
              <div style={{ background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '12px', padding: '20px', fontSize: '12px', color: '#475569' }}>
                Self-Healing Links monitor your destination URLs 24/7. If your site goes down, RDRTO switches to your backup URL instantly. 
                {report.links.some(l => l.healing_enrolled) 
                  ? <span> You have <strong>{report.links.filter(l => l.healing_enrolled).length}</strong> links protected.</span>
                  : <span> Upgrade to Pro or Business to enable this protection.</span>}
              </div>

              <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', fontSize: '11px', color: '#64748b' }}>
                Manage your dashboard: <a href="https://rdrto.com/dashboard" style={{ color: '#4f46e5' }}>rdrto.com/dashboard</a><br/>
                Kind regards, <strong>The RDRTO Team</strong>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReport;
